import React, { useState, useEffect, useCallback } from 'react';
import { API_BASE } from '../config/api';
import { 
  FileText, 
  Upload, 
  Users, 
  TrendingUp, 
  AlertTriangle,
  CheckCircle,
  Clock,
  Target,
  BookOpen,
  BarChart3,
  FileSpreadsheet,
  Sparkles,
  Building2,
  GraduationCap,
  ClipboardList,
  ChevronRight,
  Loader2,
  Plus,
  Eye,
  Download,
  RefreshCw,
  Square,
  CheckSquare,
  Layers,
  PieChart,
  TrendingDown,
  DollarSign,
  Activity
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import axios from 'axios';
const API = `${API_BASE}/api`;

// WSP Dashboard Component
export function WSPTab({ token }) {
  const [activeSection, setActiveSection] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [dashboard, setDashboard] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [skillsMatrix, setSkillsMatrix] = useState(null);
  const [interventions, setInterventions] = useState([]);

  const fetchDashboard = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/wsp/dashboard`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDashboard(response.data);
    } catch (error) {
      console.error('Error fetching WSP dashboard:', error);
      toast.error('Failed to load WSP dashboard');
    } finally {
      setLoading(false);
    }
  }, [token]);

  const fetchDocuments = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/wsp/documents`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDocuments(response.data.documents || []);
    } catch (error) {
      console.error('Error fetching documents:', error);
    }
  }, [token]);

  const fetchSkillsMatrix = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/wsp/skills-matrix`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSkillsMatrix(response.data);
    } catch (error) {
      console.error('Error fetching skills matrix:', error);
    }
  }, [token]);

  const fetchInterventions = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/wsp/interventions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setInterventions(response.data.interventions || []);
    } catch (error) {
      console.error('Error fetching interventions:', error);
    }
  }, [token]);

  useEffect(() => {
    fetchDashboard();
    fetchDocuments();
    fetchSkillsMatrix();
    fetchInterventions();
  }, [fetchDashboard, fetchDocuments, fetchSkillsMatrix, fetchInterventions]);

  const sections = [
    { id: 'overview', label: 'Overview', icon: BarChart3 },
    { id: 'analytics', label: 'Analytics', icon: PieChart },
    { id: 'upload', label: 'Upload WSP', icon: Upload },
    { id: 'skills', label: 'Skills Matrix', icon: Target },
    { id: 'interventions', label: 'Interventions', icon: BookOpen },
    { id: 'reports', label: 'ATR Reports', icon: FileSpreadsheet }
  ];

  if (loading && !dashboard) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="h-8 w-8 animate-spin text-navigate-gold" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white flex items-center gap-3">
            <Building2 className="h-7 w-7 text-navigate-gold" />
            Workplace Skills Plan (WSP)
          </h2>
          <p className="text-zinc-400 mt-1">
            Automate skills audits, training analysis, and SETA reporting
          </p>
        </div>
        <Button 
          onClick={fetchDashboard}
          variant="outline"
          className="border-zinc-700 text-zinc-300 hover:bg-zinc-800"
        >
          <RefreshCw className="h-4 w-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Section Tabs */}
      <div className="flex gap-2 p-1 bg-zinc-900 rounded-lg overflow-x-auto">
        {sections.map(section => (
          <button
            key={section.id}
            onClick={() => setActiveSection(section.id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-all ${
              activeSection === section.id
                ? 'bg-navigate-gold text-black'
                : 'text-zinc-400 hover:text-white hover:bg-zinc-800'
            }`}
          >
            <section.icon className="h-4 w-4" />
            {section.label}
          </button>
        ))}
      </div>

      {/* Section Content */}
      {activeSection === 'overview' && (
        <WSPOverview 
          dashboard={dashboard} 
          documents={documents}
          skillsMatrix={skillsMatrix}
          onViewDocument={(doc) => {
            setSelectedDoc(doc);
            setActiveSection('upload');
          }}
        />
      )}
      {activeSection === 'analytics' && (
        <WSPAnalytics token={token} />
      )}
      {activeSection === 'upload' && (
        <WSPUpload 
          token={token} 
          selectedDoc={selectedDoc}
          onUploadSuccess={() => {
            fetchDashboard();
            fetchDocuments();
            setSelectedDoc(null);
          }}
        />
      )}
      {activeSection === 'skills' && (
        <SkillsMatrix 
          data={skillsMatrix}
          onRefresh={fetchSkillsMatrix}
        />
      )}
      {activeSection === 'interventions' && (
        <InterventionsManager 
          interventions={interventions}
          token={token}
          onRefresh={fetchInterventions}
        />
      )}
      {activeSection === 'reports' && (
        <ATRReports token={token} />
      )}
    </div>
  );
}

// WSP Overview Component
function WSPOverview({ dashboard, documents, skillsMatrix, onViewDocument }) {
  const stats = dashboard?.stats || {};

  return (
    <div className="space-y-6">
      {/* Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard 
          icon={Users}
          label="Employees Profiled"
          value={stats.employees_profiled || 0}
          color="blue"
        />
        <StatCard 
          icon={AlertTriangle}
          label="Skills Gaps"
          value={stats.skills_gaps_identified || 0}
          color="orange"
        />
        <StatCard 
          icon={BookOpen}
          label="Active Interventions"
          value={stats.active_interventions || 0}
          color="purple"
        />
        <StatCard 
          icon={CheckCircle}
          label="Training Completed"
          value={stats.completed_training || 0}
          color="green"
        />
      </div>

      {/* Recent Documents & Quick Actions */}
      <div className="grid lg:grid-cols-2 gap-6">
        {/* Recent WSP Documents */}
        <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6">
          <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <FileText className="h-5 w-5 text-navigate-gold" />
            Recent WSP Documents
          </h3>
          {documents.length === 0 ? (
            <div className="text-center py-8">
              <Upload className="h-12 w-12 text-zinc-600 mx-auto mb-3" />
              <p className="text-zinc-400">No WSP documents uploaded yet</p>
              <p className="text-sm text-zinc-500 mt-1">Upload your first WSP to get AI-powered analysis</p>
            </div>
          ) : (
            <div className="space-y-3">
              {documents.slice(0, 5).map(doc => (
                <div 
                  key={doc.wsp_id}
                  className="flex items-center justify-between p-3 bg-zinc-800/50 rounded-lg hover:bg-zinc-800 transition-colors cursor-pointer"
                  onClick={() => onViewDocument(doc)}
                >
                  <div className="flex items-center gap-3">
                    <FileText className="h-5 w-5 text-zinc-400" />
                    <div>
                      <p className="text-white font-medium text-sm">{doc.organization_name || 'WSP Document'}</p>
                      <p className="text-xs text-zinc-500">FY {doc.fiscal_year} • {doc.filename}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`text-xs px-2 py-1 rounded ${
                      doc.status === 'analyzed' ? 'bg-green-500/20 text-green-400' :
                      doc.status === 'submitted' ? 'bg-blue-500/20 text-blue-400' :
                      'bg-zinc-700 text-zinc-400'
                    }`}>
                      {doc.status}
                    </span>
                    <ChevronRight className="h-4 w-4 text-zinc-500" />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Skills Gap Summary */}
        <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6">
          <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <Target className="h-5 w-5 text-orange-400" />
            Skills Gap Summary
          </h3>
          {skillsMatrix?.skills_gaps?.length > 0 ? (
            <div className="space-y-3">
              {skillsMatrix.skills_gaps.slice(0, 5).map((gap, idx) => (
                <div key={idx} className="flex items-center justify-between p-3 bg-zinc-800/50 rounded-lg">
                  <div>
                    <p className="text-white font-medium text-sm">
                      {typeof gap === 'object' ? (gap.skill_name || gap.skill || gap.name || 'Unknown') : gap}
                    </p>
                    <p className="text-xs text-zinc-500">
                      {gap?.affected_roles ? (Array.isArray(gap.affected_roles) ? gap.affected_roles.join(', ') : gap.affected_roles) : 'Multiple roles'}
                    </p>
                  </div>
                  <span className={`text-xs px-2 py-1 rounded font-medium ${
                    (gap?.gap_severity || gap?.severity) === 'Critical' ? 'bg-red-500/20 text-red-400' :
                    (gap?.gap_severity || gap?.severity) === 'High' ? 'bg-orange-500/20 text-orange-400' :
                    (gap?.gap_severity || gap?.severity) === 'Medium' ? 'bg-yellow-500/20 text-yellow-400' :
                    'bg-zinc-700 text-zinc-400'
                  }`}>
                    {gap?.gap_severity || gap?.severity || 'Medium'}
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <Target className="h-12 w-12 text-zinc-600 mx-auto mb-3" />
              <p className="text-zinc-400">No skills gaps analyzed yet</p>
              <p className="text-sm text-zinc-500 mt-1">Upload a WSP document for AI analysis</p>
            </div>
          )}
        </div>
      </div>

      {/* Feature Cards */}
      <div className="grid md:grid-cols-3 gap-4">
        <FeatureCard 
          icon={Sparkles}
          title="AI-Powered Analysis"
          description="Upload your WSP and let NAVI identify skills gaps and recommend interventions"
        />
        <FeatureCard 
          icon={ClipboardList}
          title="SETA-Ready Reports"
          description="Generate compliance-ready ATR reports with audit evidence"
        />
        <FeatureCard 
          icon={GraduationCap}
          title="Learning Interventions"
          description="Match skills gaps to courses, workshops, and certifications"
        />
      </div>
    </div>
  );
}

// WSP Analytics Dashboard Component
function WSPAnalytics({ token }) {
  const [loading, setLoading] = useState(true);
  const [analytics, setAnalytics] = useState(null);
  const [interventionProgress, setInterventionProgress] = useState(null);
  const [closureTrend, setClosureTrend] = useState(null);

  const fetchAnalytics = useCallback(async () => {
    try {
      setLoading(true);
      const [dashboardRes, progressRes, trendRes] = await Promise.all([
        axios.get(`${API}/wsp/analytics/dashboard`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/wsp/analytics/intervention-progress`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/wsp/analytics/skills-closure-trend`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      setAnalytics(dashboardRes.data);
      setInterventionProgress(progressRes.data);
      setClosureTrend(trendRes.data);
    } catch (error) {
      console.error('Error fetching analytics:', error);
      toast.error('Failed to load analytics data');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchAnalytics();
  }, [fetchAnalytics]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="h-8 w-8 animate-spin text-navigate-gold" />
      </div>
    );
  }

  if (!analytics) {
    return (
      <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-8 text-center">
        <PieChart className="h-12 w-12 text-zinc-600 mx-auto mb-4" />
        <h3 className="text-xl font-medium text-white mb-2">No Analytics Data</h3>
        <p className="text-zinc-400">Upload WSP documents to see analytics</p>
      </div>
    );
  }

  const { overview, intervention_status, intervention_priority, training_metrics, roi_metrics, skills_gap_by_severity } = analytics;

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-ZA', { style: 'currency', currency: 'ZAR', maximumFractionDigits: 0 }).format(amount);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-white flex items-center gap-2">
            <PieChart className="h-5 w-5 text-navigate-gold" />
            WSP Analytics Dashboard
          </h3>
          <p className="text-sm text-zinc-400 mt-1">
            Track skills gap closure, intervention progress, and training ROI
          </p>
        </div>
        <Button 
          onClick={fetchAnalytics}
          variant="outline"
          className="border-zinc-700 text-zinc-300 hover:bg-zinc-800"
        >
          <RefreshCw className="h-4 w-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Skills Gap Closure Overview */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-gradient-to-br from-emerald-500/20 to-emerald-600/10 border border-emerald-500/30 rounded-xl p-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-emerald-400 text-sm font-medium">Skills Gap Closure Rate</p>
              <p className="text-3xl font-bold text-white mt-1">{overview.closure_rate_percent}%</p>
              <p className="text-xs text-zinc-400 mt-1">
                {overview.gaps_addressed} of {overview.total_skills_gaps} gaps addressed
              </p>
            </div>
            <div className="h-14 w-14 bg-emerald-500/20 rounded-full flex items-center justify-center">
              <TrendingUp className="h-7 w-7 text-emerald-400" />
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-purple-500/20 to-purple-600/10 border border-purple-500/30 rounded-xl p-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-purple-400 text-sm font-medium">Course Coverage</p>
              <p className="text-3xl font-bold text-white mt-1">{overview.course_coverage_percent}%</p>
              <p className="text-xs text-zinc-400 mt-1">
                {overview.interventions_with_courses} of {overview.total_interventions} with courses
              </p>
            </div>
            <div className="h-14 w-14 bg-purple-500/20 rounded-full flex items-center justify-center">
              <BookOpen className="h-7 w-7 text-purple-400" />
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-blue-500/20 to-blue-600/10 border border-blue-500/30 rounded-xl p-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-blue-400 text-sm font-medium">Intervention Completion</p>
              <p className="text-3xl font-bold text-white mt-1">{intervention_status.completion_rate_percent}%</p>
              <p className="text-xs text-zinc-400 mt-1">
                {intervention_status.completed} of {overview.total_interventions} completed
              </p>
            </div>
            <div className="h-14 w-14 bg-blue-500/20 rounded-full flex items-center justify-center">
              <CheckCircle className="h-7 w-7 text-blue-400" />
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-amber-500/20 to-amber-600/10 border border-amber-500/30 rounded-xl p-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-amber-400 text-sm font-medium">Est. Cost Savings</p>
              <p className="text-2xl font-bold text-white mt-1">{formatCurrency(roi_metrics.estimated_cost_savings)}</p>
              <p className="text-xs text-zinc-400 mt-1">
                vs external training
              </p>
            </div>
            <div className="h-14 w-14 bg-amber-500/20 rounded-full flex items-center justify-center">
              <DollarSign className="h-7 w-7 text-amber-400" />
            </div>
          </div>
        </div>
      </div>

      {/* Skills Gap Severity & Intervention Status */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Skills Gap by Severity */}
        <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-5">
          <h4 className="text-white font-medium mb-4 flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 text-navigate-gold" />
            Skills Gaps by Severity
          </h4>
          <div className="space-y-3">
            {skills_gap_by_severity.map((item, idx) => (
              <div key={idx} className="flex items-center gap-3">
                <div className="w-20 text-sm text-zinc-400">{item.severity}</div>
                <div className="flex-1 bg-zinc-800 rounded-full h-6 overflow-hidden">
                  <div 
                    className="h-full rounded-full transition-all duration-500"
                    style={{ 
                      width: `${overview.total_skills_gaps > 0 ? (item.count / overview.total_skills_gaps * 100) : 0}%`,
                      backgroundColor: item.color 
                    }}
                  />
                </div>
                <div className="w-12 text-right text-sm font-medium text-white">{item.count}</div>
              </div>
            ))}
          </div>
          <div className="mt-4 pt-4 border-t border-zinc-800">
            <div className="flex justify-between text-sm">
              <span className="text-zinc-400">Total Skills Gaps</span>
              <span className="text-white font-medium">{overview.total_skills_gaps}</span>
            </div>
            <div className="flex justify-between text-sm mt-1">
              <span className="text-zinc-400">Critical Gaps</span>
              <span className="text-red-400 font-medium">{overview.critical_gaps}</span>
            </div>
          </div>
        </div>

        {/* Intervention Status */}
        <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-5">
          <h4 className="text-white font-medium mb-4 flex items-center gap-2">
            <Activity className="h-4 w-4 text-navigate-gold" />
            Intervention Status
          </h4>
          <div className="space-y-3">
            <div className="flex items-center gap-3">
              <div className="w-24 text-sm text-zinc-400">Planned</div>
              <div className="flex-1 bg-zinc-800 rounded-full h-6 overflow-hidden">
                <div 
                  className="h-full bg-zinc-500 rounded-full transition-all duration-500"
                  style={{ width: `${overview.total_interventions > 0 ? (intervention_status.planned / overview.total_interventions * 100) : 0}%` }}
                />
              </div>
              <div className="w-12 text-right text-sm font-medium text-white">{intervention_status.planned}</div>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-24 text-sm text-zinc-400">In Progress</div>
              <div className="flex-1 bg-zinc-800 rounded-full h-6 overflow-hidden">
                <div 
                  className="h-full bg-blue-500 rounded-full transition-all duration-500"
                  style={{ width: `${overview.total_interventions > 0 ? (intervention_status.in_progress / overview.total_interventions * 100) : 0}%` }}
                />
              </div>
              <div className="w-12 text-right text-sm font-medium text-white">{intervention_status.in_progress}</div>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-24 text-sm text-zinc-400">Completed</div>
              <div className="flex-1 bg-zinc-800 rounded-full h-6 overflow-hidden">
                <div 
                  className="h-full bg-emerald-500 rounded-full transition-all duration-500"
                  style={{ width: `${overview.total_interventions > 0 ? (intervention_status.completed / overview.total_interventions * 100) : 0}%` }}
                />
              </div>
              <div className="w-12 text-right text-sm font-medium text-white">{intervention_status.completed}</div>
            </div>
          </div>
          <div className="mt-4 pt-4 border-t border-zinc-800">
            <div className="flex justify-between text-sm">
              <span className="text-zinc-400">Total Interventions</span>
              <span className="text-white font-medium">{overview.total_interventions}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Intervention Priority & Training Metrics */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Priority Breakdown */}
        <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-5">
          <h4 className="text-white font-medium mb-4 flex items-center gap-2">
            <Target className="h-4 w-4 text-navigate-gold" />
            Intervention Progress by Priority
          </h4>
          {interventionProgress && (
            <div className="space-y-4">
              {['critical', 'high', 'medium', 'low'].map(priority => {
                const data = interventionProgress.by_priority[priority];
                if (!data || data.total === 0) return null;
                return (
                  <div key={priority} className="bg-zinc-800/50 rounded-lg p-3">
                    <div className="flex items-center justify-between mb-2">
                      <span className={`text-sm font-medium capitalize ${
                        priority === 'critical' ? 'text-red-400' :
                        priority === 'high' ? 'text-orange-400' :
                        priority === 'medium' ? 'text-yellow-400' : 'text-zinc-400'
                      }`}>
                        {priority}
                      </span>
                      <span className="text-xs text-zinc-500">
                        {data.completed}/{data.total} completed ({data.completion_percent}%)
                      </span>
                    </div>
                    <div className="flex gap-1 h-2">
                      <div 
                        className="bg-emerald-500 rounded-l"
                        style={{ width: `${data.total > 0 ? (data.completed / data.total * 100) : 0}%` }}
                      />
                      <div 
                        className="bg-blue-500"
                        style={{ width: `${data.total > 0 ? (data.in_progress / data.total * 100) : 0}%` }}
                      />
                      <div 
                        className="bg-zinc-600 rounded-r flex-1"
                      />
                    </div>
                    <div className="flex justify-between mt-2 text-xs text-zinc-500">
                      <span>{data.with_course} with courses</span>
                      <span>{data.planned} planned</span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Training Metrics */}
        <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-5">
          <h4 className="text-white font-medium mb-4 flex items-center gap-2">
            <GraduationCap className="h-4 w-4 text-navigate-gold" />
            Training Metrics
          </h4>
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-zinc-800/50 rounded-lg p-4 text-center">
              <p className="text-2xl font-bold text-white">{training_metrics.courses_created}</p>
              <p className="text-xs text-zinc-400 mt-1">Courses Created</p>
            </div>
            <div className="bg-zinc-800/50 rounded-lg p-4 text-center">
              <p className="text-2xl font-bold text-white">{training_metrics.courses_ready}</p>
              <p className="text-xs text-zinc-400 mt-1">Courses Ready</p>
            </div>
            <div className="bg-zinc-800/50 rounded-lg p-4 text-center">
              <p className="text-2xl font-bold text-white">{training_metrics.total_training_hours_planned}</p>
              <p className="text-xs text-zinc-400 mt-1">Training Hours</p>
            </div>
            <div className="bg-zinc-800/50 rounded-lg p-4 text-center">
              <p className="text-2xl font-bold text-white">{training_metrics.total_enrollments}</p>
              <p className="text-xs text-zinc-400 mt-1">Enrollments</p>
            </div>
          </div>
          {training_metrics.courses_generating > 0 && (
            <div className="mt-4 p-3 bg-purple-500/10 border border-purple-500/30 rounded-lg flex items-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin text-purple-400" />
              <span className="text-sm text-purple-300">
                {training_metrics.courses_generating} courses generating...
              </span>
            </div>
          )}
        </div>
      </div>

      {/* ROI Breakdown */}
      <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-5">
        <h4 className="text-white font-medium mb-4 flex items-center gap-2">
          <DollarSign className="h-4 w-4 text-navigate-gold" />
          Training ROI Analysis
        </h4>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4">
            <p className="text-sm text-red-400 font-medium">External Training Cost</p>
            <p className="text-2xl font-bold text-white mt-1">{formatCurrency(roi_metrics.estimated_external_training_cost)}</p>
            <p className="text-xs text-zinc-500 mt-1">@ {formatCurrency(roi_metrics.cost_per_hour_external)}/hour</p>
          </div>
          <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-4">
            <p className="text-sm text-emerald-400 font-medium">Internal LMS Cost</p>
            <p className="text-2xl font-bold text-white mt-1">{formatCurrency(roi_metrics.estimated_internal_training_cost)}</p>
            <p className="text-xs text-zinc-500 mt-1">@ {formatCurrency(roi_metrics.cost_per_hour_internal)}/hour</p>
          </div>
          <div className="bg-navigate-gold/10 border border-navigate-gold/30 rounded-lg p-4">
            <p className="text-sm text-navigate-gold font-medium">Estimated Savings</p>
            <p className="text-2xl font-bold text-white mt-1">{formatCurrency(roi_metrics.estimated_cost_savings)}</p>
            <p className="text-xs text-zinc-500 mt-1">
              {roi_metrics.estimated_external_training_cost > 0 
                ? `${Math.round((roi_metrics.estimated_cost_savings / roi_metrics.estimated_external_training_cost) * 100)}% reduction`
                : 'N/A'}
            </p>
          </div>
        </div>
      </div>

      {/* Interventions Needing Attention */}
      {interventionProgress?.needing_attention?.length > 0 && (
        <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-5">
          <h4 className="text-white font-medium mb-4 flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 text-orange-400" />
            Interventions Needing Attention
            <span className="text-xs px-2 py-0.5 bg-orange-500/20 text-orange-400 rounded-full ml-2">
              {interventionProgress.needing_attention.length}
            </span>
          </h4>
          <p className="text-sm text-zinc-400 mb-4">
            Critical and High priority interventions without courses
          </p>
          <div className="grid gap-3">
            {interventionProgress.needing_attention.slice(0, 5).map((int, idx) => (
              <div key={int.intervention_id || idx} className="flex items-center justify-between bg-zinc-800/50 rounded-lg p-3">
                <div className="flex-1">
                  <p className="text-white text-sm font-medium">{int.title}</p>
                  <div className="flex items-center gap-2 mt-1">
                    <span className={`text-xs px-2 py-0.5 rounded ${
                      int.priority === 'Critical' ? 'bg-red-500/20 text-red-400' : 'bg-orange-500/20 text-orange-400'
                    }`}>
                      {int.priority}
                    </span>
                    <span className="text-xs text-zinc-500">{int.type}</span>
                    <span className="text-xs text-zinc-500">{int.duration_hours}h</span>
                  </div>
                </div>
                <Button size="sm" className="bg-purple-600 hover:bg-purple-500 text-white text-xs">
                  <Sparkles className="h-3 w-3 mr-1" />
                  Create Course
                </Button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Skills Closure Trend */}
      {closureTrend?.trend?.length > 0 && (
        <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-5">
          <h4 className="text-white font-medium mb-4 flex items-center gap-2">
            <TrendingUp className="h-4 w-4 text-navigate-gold" />
            Skills Gap Closure Trend
          </h4>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="text-left border-b border-zinc-800">
                  <th className="pb-2 text-xs text-zinc-500 font-medium">Month</th>
                  <th className="pb-2 text-xs text-zinc-500 font-medium text-right">Interventions Started</th>
                  <th className="pb-2 text-xs text-zinc-500 font-medium text-right">Gaps Addressed</th>
                  <th className="pb-2 text-xs text-zinc-500 font-medium text-right">Cumulative</th>
                  <th className="pb-2 text-xs text-zinc-500 font-medium text-right">Closure Rate</th>
                </tr>
              </thead>
              <tbody>
                {closureTrend.trend.map((row, idx) => (
                  <tr key={idx} className="border-b border-zinc-800/50">
                    <td className="py-2 text-sm text-white">{row.month}</td>
                    <td className="py-2 text-sm text-zinc-300 text-right">{row.interventions_started}</td>
                    <td className="py-2 text-sm text-zinc-300 text-right">{row.gaps_addressed_this_month}</td>
                    <td className="py-2 text-sm text-zinc-300 text-right">{row.cumulative_gaps_addressed}</td>
                    <td className="py-2 text-right">
                      <span className="text-sm font-medium text-emerald-400">{row.closure_rate_percent}%</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

// WSP Upload Component
function WSPUpload({ token, selectedDoc, onUploadSuccess }) {
  const [uploading, setUploading] = useState(false);
  const [file, setFile] = useState(null);
  const [formData, setFormData] = useState({
    fiscal_year: new Date().getFullYear().toString(),
    organization_name: '',
    sector: '',
    seta: ''
  });
  const [analysisResult, setAnalysisResult] = useState(selectedDoc || null);
  const [pollingId, setPollingId] = useState(null);

  // Poll for analysis completion
  const pollForCompletion = async (wspId) => {
    try {
      const response = await axios.get(`${API}/wsp/documents/${wspId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.status === 'analyzed') {
        setAnalysisResult(response.data);
        toast.success('AI analysis complete!');
        onUploadSuccess();
        return true; // Stop polling
      } else if (response.data.status === 'error') {
        toast.error('Analysis failed: ' + (response.data.error_message || 'Unknown error'));
        setAnalysisResult(response.data);
        return true; // Stop polling
      }
      
      // Still processing
      setAnalysisResult(response.data);
      return false; // Continue polling
    } catch (error) {
      console.error('Polling error:', error);
      return false;
    }
  };

  useEffect(() => {
    let intervalId;
    if (pollingId) {
      intervalId = setInterval(async () => {
        const done = await pollForCompletion(pollingId);
        if (done) {
          setPollingId(null);
          clearInterval(intervalId);
        }
      }, 5000); // Poll every 5 seconds
    }
    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [pollingId, token]);

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!file) {
      toast.error('Please select a file to upload');
      return;
    }
    if (!formData.organization_name) {
      toast.error('Please enter your organization name');
      return;
    }

    setUploading(true);
    try {
      const data = new FormData();
      data.append('file', file);
      data.append('fiscal_year', formData.fiscal_year);
      data.append('organization_name', formData.organization_name);
      data.append('sector', formData.sector);
      data.append('seta', formData.seta);

      const response = await axios.post(`${API}/wsp/upload`, data, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        },
        timeout: 30000 // 30 second timeout for upload
      });

      if (response.data.status === 'processing') {
        toast.success('WSP uploaded! AI analysis in progress (1-2 minutes)...');
        setAnalysisResult(response.data.document);
        setPollingId(response.data.wsp_id); // Start polling
      } else {
        toast.success('WSP uploaded and analyzed successfully!');
        setAnalysisResult(response.data.document);
        onUploadSuccess();
      }
      setFile(null);
    } catch (error) {
      console.error('Upload error:', error);
      toast.error(error.response?.data?.detail || 'Failed to upload WSP');
    } finally {
      setUploading(false);
    }
  };

  const setaOptions = [
    'BANKSETA', 'CATHSSETA', 'CETA', 'CHIETA', 'ETDP SETA', 'EWSETA',
    'FASSET', 'FP&M SETA', 'FOODBEV SETA', 'HWSETA', 'INSETA', 'LGSETA',
    'MICT SETA', 'MQA', 'MERSETA', 'PSETA', 'QCTO', 'SASSETA', 'SERVICES SETA',
    'TETA', 'W&RSETA', 'AgriSETA'
  ];

  return (
    <div className="space-y-6">
      <div className="grid lg:grid-cols-2 gap-6">
        {/* Upload Form */}
        <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6">
          <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <Upload className="h-5 w-5 text-navigate-gold" />
            Upload WSP Document
          </h3>
          <form onSubmit={handleUpload} className="space-y-4">
            <div>
              <Label className="text-zinc-300">Organization Name *</Label>
              <Input
                value={formData.organization_name}
                onChange={(e) => setFormData(prev => ({ ...prev, organization_name: e.target.value }))}
                placeholder="Enter your organization name"
                className="mt-1 bg-zinc-800 border-zinc-700"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-zinc-300">Fiscal Year</Label>
                <select
                  value={formData.fiscal_year}
                  onChange={(e) => setFormData(prev => ({ ...prev, fiscal_year: e.target.value }))}
                  className="w-full mt-1 bg-zinc-800 border border-zinc-700 rounded-md px-3 py-2 text-white"
                >
                  {[2026, 2025, 2024, 2023].map(year => (
                    <option key={year} value={year}>{year}</option>
                  ))}
                </select>
              </div>
              <div>
                <Label className="text-zinc-300">SETA</Label>
                <select
                  value={formData.seta}
                  onChange={(e) => setFormData(prev => ({ ...prev, seta: e.target.value }))}
                  className="w-full mt-1 bg-zinc-800 border border-zinc-700 rounded-md px-3 py-2 text-white"
                >
                  <option value="">Select SETA</option>
                  {setaOptions.map(seta => (
                    <option key={seta} value={seta}>{seta}</option>
                  ))}
                </select>
              </div>
            </div>
            <div>
              <Label className="text-zinc-300">Sector/Industry</Label>
              <Input
                value={formData.sector}
                onChange={(e) => setFormData(prev => ({ ...prev, sector: e.target.value }))}
                placeholder="e.g., Financial Services, Manufacturing"
                className="mt-1 bg-zinc-800 border-zinc-700"
              />
            </div>
            <div>
              <Label className="text-zinc-300">WSP Document *</Label>
              <div className="mt-1 border-2 border-dashed border-zinc-700 rounded-lg p-6 text-center hover:border-navigate-gold/50 transition-colors">
                <input
                  type="file"
                  accept=".pdf,.doc,.docx,.txt,.xlsx"
                  onChange={(e) => setFile(e.target.files[0])}
                  className="hidden"
                  id="wsp-file"
                />
                <label htmlFor="wsp-file" className="cursor-pointer">
                  {file ? (
                    <div className="flex items-center justify-center gap-2 text-navigate-gold">
                      <FileText className="h-6 w-6" />
                      <span>{file.name}</span>
                    </div>
                  ) : (
                    <>
                      <Upload className="h-8 w-8 text-zinc-500 mx-auto mb-2" />
                      <p className="text-zinc-400">Click to select WSP document</p>
                      <p className="text-xs text-zinc-500 mt-1">PDF, Word, Excel, or Text files</p>
                    </>
                  )}
                </label>
              </div>
            </div>
            <Button
              type="submit"
              disabled={uploading || !file}
              className="w-full bg-navigate-gold hover:bg-navigate-gold/90 text-black"
            >
              {uploading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Analyzing with NAVI...
                </>
              ) : (
                <>
                  <Sparkles className="h-4 w-4 mr-2" />
                  Upload & Analyze
                </>
              )}
            </Button>
          </form>
        </div>

        {/* Analysis Results */}
        <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6">
          <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-purple-400" />
            NAVI Analysis Results
          </h3>
          {analysisResult ? (
            <div className="space-y-4">
              {/* Processing Status */}
              {analysisResult.status === 'processing' && (
                <div className="p-4 bg-blue-500/10 border border-blue-500/30 rounded-lg">
                  <div className="flex items-center gap-3">
                    <Loader2 className="h-5 w-5 animate-spin text-blue-400" />
                    <div>
                      <p className="text-blue-400 font-medium">AI Analysis in Progress</p>
                      <p className="text-sm text-zinc-400">NAVI is analyzing your WSP document. This typically takes 1-2 minutes...</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Error Status */}
              {analysisResult.status === 'error' && (
                <div className="p-4 bg-red-500/10 border border-red-500/30 rounded-lg">
                  <p className="text-red-400 font-medium">Analysis Failed</p>
                  <p className="text-sm text-zinc-400">{analysisResult.error_message || 'Please try uploading again.'}</p>
                </div>
              )}

              {/* Summary - only show when analyzed */}
              {analysisResult.status === 'analyzed' && (
                <div className="p-4 bg-purple-500/10 border border-purple-500/30 rounded-lg">
                  <p className="text-zinc-300 text-sm">
                    {analysisResult.ai_analysis?.executive_summary || 'Analysis complete. Review the insights below.'}
                  </p>
                </div>
              )}

              {/* Skills Gaps */}
              {analysisResult.skills_gaps?.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium text-zinc-300 mb-2">
                    Skills Gaps Identified ({analysisResult.skills_gaps.length})
                  </h4>
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {analysisResult.skills_gaps.map((gap, idx) => (
                      <div key={idx} className="flex items-center justify-between p-2 bg-zinc-800 rounded">
                        <span className="text-sm text-white">
                          {typeof gap === 'object' ? (gap.skill_name || gap.skill || gap.name || 'Unknown skill') : gap}
                        </span>
                        <span className={`text-xs px-2 py-0.5 rounded ${
                          (gap?.gap_severity || gap?.severity) === 'Critical' ? 'bg-red-500/20 text-red-400' :
                          (gap?.gap_severity || gap?.severity) === 'High' ? 'bg-orange-500/20 text-orange-400' :
                          'bg-yellow-500/20 text-yellow-400'
                        }`}>
                          {gap?.gap_severity || gap?.severity || 'Medium'}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Recommended Interventions */}
              {analysisResult.recommended_interventions?.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium text-zinc-300 mb-2">
                    Recommended Interventions ({analysisResult.recommended_interventions.length})
                  </h4>
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {analysisResult.recommended_interventions.slice(0, 5).map((int, idx) => (
                      <div key={idx} className="p-2 bg-zinc-800 rounded">
                        <p className="text-sm text-white font-medium">
                          {typeof int === 'object' ? (int.skill_gap || int.title || int.skill || 'Intervention') : int}
                        </p>
                        {int?.interventions?.slice(0, 2).map((i, iIdx) => (
                          <p key={iIdx} className="text-xs text-zinc-400 mt-1">
                            • {typeof i === 'object' ? (i.title || i.name || 'Intervention') : i} 
                            {i?.type ? ` (${i.type})` : ''}
                          </p>
                        ))}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Scarce Skills */}
              {analysisResult.ai_analysis?.scarce_skills_identified?.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium text-zinc-300 mb-2">Scarce Skills Identified</h4>
                  <div className="flex flex-wrap gap-2">
                    {analysisResult.ai_analysis.scarce_skills_identified.map((skill, idx) => (
                      <span key={idx} className="text-xs px-2 py-1 bg-red-500/20 text-red-400 rounded">
                        {typeof skill === 'object' ? (skill.skill || skill.name || JSON.stringify(skill)) : skill}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-12">
              <FileText className="h-12 w-12 text-zinc-600 mx-auto mb-3" />
              <p className="text-zinc-400">Upload a WSP document to see AI analysis</p>
              <p className="text-sm text-zinc-500 mt-1">
                NAVI will identify skills gaps and recommend learning interventions
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// Skills Matrix Component
function SkillsMatrix({ data, onRefresh }) {
  if (!data || data.matrix?.length === 0) {
    return (
      <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-8 text-center">
        <Target className="h-12 w-12 text-zinc-600 mx-auto mb-4" />
        <h3 className="text-xl font-medium text-white mb-2">No Skills Data Yet</h3>
        <p className="text-zinc-400 mb-4">
          Upload a WSP document or import employees to build your skills matrix
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Summary Stats */}
      <div className="grid grid-cols-4 gap-4">
        <StatCard 
          icon={Target}
          label="Skills Tracked"
          value={data.summary?.total_skills_tracked || 0}
          color="blue"
        />
        <StatCard 
          icon={AlertTriangle}
          label="Critical Skills"
          value={data.summary?.critical_skills || 0}
          color="red"
        />
        <StatCard 
          icon={TrendingUp}
          label="Scarce Skills"
          value={data.summary?.scarce_skills || 0}
          color="orange"
        />
        <StatCard 
          icon={CheckCircle}
          label="Total Gaps"
          value={data.summary?.total_gaps || 0}
          color="purple"
        />
      </div>

      {/* Skills Gaps Table */}
      <div className="bg-zinc-900 border border-zinc-800 rounded-xl overflow-hidden">
        <div className="px-6 py-4 border-b border-zinc-800 flex items-center justify-between">
          <h3 className="text-lg font-semibold text-white">Skills Gap Analysis</h3>
          <Button variant="outline" size="sm" onClick={onRefresh} className="border-zinc-700">
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-zinc-800">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-zinc-400 uppercase">Skill</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-zinc-400 uppercase">Severity</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-zinc-400 uppercase">Affected Roles</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-zinc-400 uppercase">Description</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-800">
              {data.skills_gaps?.map((gap, idx) => (
                <tr key={idx} className="hover:bg-zinc-800/50">
                  <td className="px-6 py-4 text-white font-medium">
                    {typeof gap === 'object' ? (gap.skill_name || gap.skill || gap.name || 'Unknown') : gap}
                  </td>
                  <td className="px-6 py-4">
                    <span className={`text-xs px-2 py-1 rounded font-medium ${
                      (gap?.gap_severity || gap?.severity) === 'Critical' ? 'bg-red-500/20 text-red-400' :
                      (gap?.gap_severity || gap?.severity) === 'High' ? 'bg-orange-500/20 text-orange-400' :
                      (gap?.gap_severity || gap?.severity) === 'Medium' ? 'bg-yellow-500/20 text-yellow-400' :
                      'bg-zinc-700 text-zinc-400'
                    }`}>
                      {gap?.gap_severity || gap?.severity || 'Medium'}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-zinc-300 text-sm">
                    {gap?.affected_roles ? (Array.isArray(gap.affected_roles) ? gap.affected_roles.join(', ') : String(gap.affected_roles)) : '-'}
                  </td>
                  <td className="px-6 py-4 text-zinc-400 text-sm max-w-xs truncate">
                    {gap?.description || '-'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// Interventions Manager Component
function InterventionsManager({ interventions, token, onRefresh }) {
  const [creatingCourseId, setCreatingCourseId] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkCreating, setBulkCreating] = useState(false);

  // Filter interventions that can have courses created (no linked course)
  const eligibleInterventions = interventions.filter(int => int?.intervention_id && !int?.linked_course_id);

  const updateStatus = async (interventionId, newStatus) => {
    try {
      await axios.put(`${API}/wsp/interventions/${interventionId}`, 
        { status: newStatus },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      toast.success('Intervention updated');
      onRefresh();
    } catch (error) {
      toast.error('Failed to update intervention');
    }
  };

  const createCourseFromIntervention = async (interventionId, interventionTitle) => {
    try {
      setCreatingCourseId(interventionId);
      const response = await axios.post(
        `${API}/wsp/interventions/${interventionId}/create-course`,
        {},
        { headers: { Authorization: `Bearer ${token}` }}
      );
      
      if (response.data.success) {
        toast.success(`Course "${interventionTitle}" is being generated! Check the Courses page to see progress.`);
        onRefresh();
      }
    } catch (error) {
      console.error('Create course error:', error);
      toast.error(error.response?.data?.detail || 'Failed to create course');
    } finally {
      setCreatingCourseId(null);
    }
  };

  const toggleSelection = (interventionId) => {
    setSelectedIds(prev => 
      prev.includes(interventionId)
        ? prev.filter(id => id !== interventionId)
        : [...prev, interventionId]
    );
  };

  const selectAll = () => {
    if (selectedIds.length === eligibleInterventions.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(eligibleInterventions.map(int => int.intervention_id));
    }
  };

  const bulkCreateCourses = async () => {
    if (selectedIds.length === 0) {
      toast.error('Please select at least one intervention');
      return;
    }

    if (selectedIds.length > 10) {
      toast.error('Maximum 10 interventions can be processed at once');
      return;
    }

    try {
      setBulkCreating(true);
      const response = await axios.post(
        `${API}/wsp/interventions/bulk-create-courses`,
        { intervention_ids: selectedIds },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      
      if (response.data.success) {
        toast.success(`${response.data.interventions_queued} courses are being generated! Check the Courses page to see progress.`);
        setSelectedIds([]);
        onRefresh();
      }
    } catch (error) {
      console.error('Bulk create error:', error);
      toast.error(error.response?.data?.detail || 'Failed to create courses');
    } finally {
      setBulkCreating(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header with bulk actions */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h3 className="text-lg font-semibold text-white">Training Interventions</h3>
          <p className="text-sm text-zinc-400 mt-1">
            Select interventions to bulk create courses, or create individually
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button className="bg-navigate-gold hover:bg-navigate-gold/90 text-black">
            <Plus className="h-4 w-4 mr-2" />
            Add Intervention
          </Button>
        </div>
      </div>

      {interventions.length === 0 ? (
        <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-8 text-center">
          <BookOpen className="h-12 w-12 text-zinc-600 mx-auto mb-4" />
          <h3 className="text-xl font-medium text-white mb-2">No Interventions Yet</h3>
          <p className="text-zinc-400">
            Interventions will be suggested when you upload a WSP document
          </p>
        </div>
      ) : (
        <>
          {/* Bulk Selection Bar */}
          {eligibleInterventions.length > 0 && (
            <div className="bg-zinc-800/50 border border-zinc-700 rounded-lg p-3 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <button
                  onClick={selectAll}
                  className="flex items-center gap-2 text-sm text-zinc-300 hover:text-white transition-colors"
                >
                  {selectedIds.length === eligibleInterventions.length ? (
                    <CheckSquare className="h-5 w-5 text-navigate-gold" />
                  ) : selectedIds.length > 0 ? (
                    <div className="h-5 w-5 border-2 border-navigate-gold bg-navigate-gold/30 rounded flex items-center justify-center">
                      <div className="h-2 w-2 bg-navigate-gold rounded-sm" />
                    </div>
                  ) : (
                    <Square className="h-5 w-5" />
                  )}
                  {selectedIds.length === eligibleInterventions.length 
                    ? 'Deselect All' 
                    : `Select All (${eligibleInterventions.length} eligible)`}
                </button>
                {selectedIds.length > 0 && (
                  <span className="text-sm text-navigate-gold font-medium">
                    {selectedIds.length} selected
                  </span>
                )}
              </div>
              {selectedIds.length > 0 && (
                <Button
                  onClick={bulkCreateCourses}
                  disabled={bulkCreating}
                  className="bg-purple-600 hover:bg-purple-500 text-white"
                >
                  {bulkCreating ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Creating {selectedIds.length} Courses...
                    </>
                  ) : (
                    <>
                      <Layers className="h-4 w-4 mr-2" />
                      Bulk Create {selectedIds.length} Courses
                    </>
                  )}
                </Button>
              )}
            </div>
          )}

          {/* Interventions List */}
          <div className="grid gap-4">
            {interventions.map((int, idx) => {
              const isEligible = int?.intervention_id && !int?.linked_course_id;
              const isSelected = selectedIds.includes(int?.intervention_id);
              
              return (
                <div 
                  key={int?.intervention_id || idx} 
                  className={`bg-zinc-900 border rounded-xl p-4 transition-all ${
                    isSelected 
                      ? 'border-navigate-gold/50 bg-navigate-gold/5' 
                      : 'border-zinc-800 hover:border-zinc-700'
                  }`}
                >
                  <div className="flex items-start gap-4">
                    {/* Checkbox */}
                    {isEligible && (
                      <button
                        onClick={() => toggleSelection(int.intervention_id)}
                        className="mt-1 flex-shrink-0"
                      >
                        {isSelected ? (
                          <CheckSquare className="h-5 w-5 text-navigate-gold" />
                        ) : (
                          <Square className="h-5 w-5 text-zinc-500 hover:text-zinc-300" />
                        )}
                      </button>
                    )}
                    {!isEligible && <div className="w-5" />}
                    
                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h4 className="text-white font-medium">
                          {typeof int === 'object' ? (int.title || int.name || 'Intervention') : String(int)}
                        </h4>
                        {int?.linked_course_id && (
                          <span className="text-xs px-2 py-0.5 bg-green-500/20 text-green-400 rounded">
                            Course Created
                          </span>
                        )}
                        {int?.seta_aligned && (
                          <span className="text-xs px-2 py-0.5 bg-blue-500/20 text-blue-400 rounded">
                            SETA
                          </span>
                        )}
                        {int?.qcto_aligned && (
                          <span className="text-xs px-2 py-0.5 bg-purple-500/20 text-purple-400 rounded">
                            QCTO
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-zinc-400 mt-1 line-clamp-2">
                        {int?.description || ''}
                      </p>
                      <div className="flex items-center gap-3 mt-2 flex-wrap">
                        <span className="text-xs px-2 py-1 bg-zinc-800 rounded text-zinc-300">
                          {int?.intervention_type || int?.type || 'Training'}
                        </span>
                        <span className="text-xs text-zinc-500">
                          {int?.duration_hours || int?.estimated_duration_hours || '—'} hours
                        </span>
                        <span className={`text-xs px-2 py-1 rounded ${
                          int?.priority === 'Critical' ? 'bg-red-500/20 text-red-400' :
                          int?.priority === 'High' ? 'bg-orange-500/20 text-orange-400' :
                          'bg-zinc-700 text-zinc-400'
                        }`}>
                          {int?.priority || 'Medium'}
                        </span>
                        {int?.target_skills?.length > 0 && (
                          <span className="text-xs text-zinc-500 truncate max-w-[200px]">
                            Skills: {int.target_skills.join(', ')}
                          </span>
                        )}
                      </div>
                    </div>
                    
                    {/* Actions */}
                    <div className="flex items-center gap-2 flex-shrink-0">
                      {isEligible && (
                        <Button
                          size="sm"
                          onClick={() => createCourseFromIntervention(int.intervention_id, int.title)}
                          disabled={creatingCourseId === int.intervention_id || bulkCreating}
                          className="bg-purple-600 hover:bg-purple-500 text-white text-xs"
                        >
                          {creatingCourseId === int.intervention_id ? (
                            <>
                              <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                              Creating...
                            </>
                          ) : (
                            <>
                              <Sparkles className="h-3 w-3 mr-1" />
                              Create Course
                            </>
                          )}
                        </Button>
                      )}
                      {int?.linked_course_id && (
                        <a
                          href={`/admin?tab=courses`}
                          className="text-xs px-3 py-1.5 bg-green-600 hover:bg-green-500 text-white rounded-md flex items-center gap-1"
                        >
                          <Eye className="h-3 w-3" />
                          View Course
                        </a>
                      )}
                      {int?.intervention_id && (
                        <select
                          value={int?.status || 'planned'}
                          onChange={(e) => updateStatus(int.intervention_id, e.target.value)}
                          className="bg-zinc-800 border border-zinc-700 rounded px-2 py-1 text-sm text-white"
                        >
                          <option value="planned">Planned</option>
                          <option value="in_progress">In Progress</option>
                          <option value="completed">Completed</option>
                        </select>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}

// ATR Reports Component
function ATRReports({ token }) {
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear().toString());
  const [loading, setLoading] = useState(false);
  const [report, setReport] = useState(null);

  const generateReport = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/wsp/atr-report?fiscal_year=${selectedYear}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setReport(response.data);
      toast.success('ATR report generated');
    } catch (error) {
      toast.error('Failed to generate report');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6">
        <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
          <FileSpreadsheet className="h-5 w-5 text-navigate-gold" />
          Annual Training Report (ATR)
        </h3>
        <div className="flex items-end gap-4">
          <div>
            <Label className="text-zinc-300">Fiscal Year</Label>
            <select
              value={selectedYear}
              onChange={(e) => setSelectedYear(e.target.value)}
              className="w-full mt-1 bg-zinc-800 border border-zinc-700 rounded-md px-3 py-2 text-white"
            >
              {[2026, 2025, 2024, 2023].map(year => (
                <option key={year} value={year}>{year}</option>
              ))}
            </select>
          </div>
          <Button 
            onClick={generateReport}
            disabled={loading}
            className="bg-navigate-gold hover:bg-navigate-gold/90 text-black"
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <BarChart3 className="h-4 w-4 mr-2" />}
            Generate Report
          </Button>
        </div>
      </div>

      {report && (
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard 
            icon={Users}
            label="Employees Trained"
            value={report.report_data?.employees_trained || 0}
            color="blue"
          />
          <StatCard 
            icon={Clock}
            label="Total Training Hours"
            value={report.report_data?.total_training_hours || 0}
            color="green"
          />
          <StatCard 
            icon={CheckCircle}
            label="Interventions Completed"
            value={report.report_data?.interventions_completed || 0}
            color="purple"
          />
          <StatCard 
            icon={GraduationCap}
            label="Compliance Training"
            value={report.report_data?.compliance_training_completed || 0}
            color="orange"
          />
        </div>
      )}
    </div>
  );
}

// Helper Components
function StatCard({ icon: Icon, label, value, color }) {
  const colors = {
    blue: 'bg-blue-500/20 text-blue-400',
    green: 'bg-green-500/20 text-green-400',
    orange: 'bg-orange-500/20 text-orange-400',
    red: 'bg-red-500/20 text-red-400',
    purple: 'bg-purple-500/20 text-purple-400'
  };

  return (
    <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4">
      <div className={`w-10 h-10 rounded-lg flex items-center justify-center mb-3 ${colors[color]}`}>
        <Icon className="h-5 w-5" />
      </div>
      <p className="text-2xl font-bold text-white">{value}</p>
      <p className="text-sm text-zinc-400">{label}</p>
    </div>
  );
}

function FeatureCard({ icon: Icon, title, description }) {
  return (
    <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-5 hover:border-navigate-gold/30 transition-colors">
      <div className="w-10 h-10 bg-navigate-gold/20 rounded-lg flex items-center justify-center mb-3">
        <Icon className="h-5 w-5 text-navigate-gold" />
      </div>
      <h4 className="text-white font-medium mb-1">{title}</h4>
      <p className="text-sm text-zinc-400">{description}</p>
    </div>
  );
}

export default WSPTab;
